import { useState, useCallback, useRef, useEffect } from "react";
import { invoiceService } from "../../../services/invoice.service";

export function useInvoiceDraft(invoiceId = null) {
  // State
  const [invoice, setInvoice] = useState({
    id: null,
    status: "draft",
    type: "goods",
    invoiceNumber: "",
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: new Date().toISOString().slice(0, 10),
    poDate: null,
    agreementNumber: null,
    poNumber: null,
    paymentTerms: "due-on-receipt",
    client: null,
    billingAddress: "",
    shippingAddress: "",
    items: [],
    notes: "",
    privateNotes: "",
    clientNotes: "",
    discount: 0,
    discountType: "flat",
    shipping: 0,
    roundOff: 0,
    total: 0,
    balanceDue: 0,
    totalPaid: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [error, setError] = useState(null);
  const [hasUserInput, setHasUserInput] = useState(false); // Track if user has entered any data

  // Refs
  const saveTimeoutRef = useRef(null);
  const hasChangesRef = useRef(false);
  const isSavingRef = useRef(false); // Track if save is in progress

  /* ═══════════════════════════════════════════════════════════
     CREATE INVOICE
     ═══════════════════════════════════════════════════════════ */
  const createInvoice = useCallback(async () => {
    if (invoice.id) return; // Already created

    setIsLoading(true);
    setError(null);

    try {
      const result = await invoiceService.create({
        type: invoice.type,
        issue_date: invoice.issueDate,
        due_date: invoice.dueDate,
        payment_terms: invoice.paymentTerms,
        status: "draft",
      });

      setInvoice((prev) => ({
        ...prev,
        id: result.id,
        invoiceNumber: result.invoice_number,
      }));

      setLastSaved(new Date());
    } catch (err) {
      console.error("Failed to create invoice:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, invoice.type, invoice.issueDate, invoice.dueDate, invoice.paymentTerms]);

  /* ═══════════════════════════════════════════════════════════
     LOAD INVOICE
     ═══════════════════════════════════════════════════════════ */
  const loadInvoice = useCallback(async (id) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await invoiceService.get(id);

      // Build client object from data
      let client = null;
      if (data.client_id) {
        // If we have client fields in the invoice, use them
        if (data.client_name) {
          client = {
            id: data.client_id,
            name: data.client_name,
            email: data.client_email,
            phone: data.client_phone,
            gstin: data.client_gstin,
            billing_address: data.client_billing_address,
            city: data.client_city,
            state: data.client_state,
            pincode: data.client_pincode,
          };
        } else {
          // Otherwise, fetch the client from the database
          try {
            const { clientService } = await import('../../../services/client.service');
            const clientData = await clientService.get(data.client_id);
            if (clientData) {
              client = {
                id: clientData.id || clientData._id,
                name: clientData.name,
                email: clientData.email,
                phone: clientData.phone,
                gstin: clientData.gstin,
                billing_address: clientData.billing_address,
                city: clientData.city,
                state: clientData.state,
                pincode: clientData.pincode,
              };
            }
          } catch (err) {
            console.warn('[useInvoiceDraft] Failed to fetch client:', err);
          }
        }
      }

      // Extract items from data - check multiple field names
      const rawItems = data.items || data.line_items || data.lineItems || [];
      
      console.log('[useInvoiceDraft] Raw invoice data:', {
        id: data.id,
        invoice_number: data.invoice_number,
        status: data.status,
        hasItems: !!data.items,
        hasLineItems: !!data.line_items,
        hasLineItemsCamel: !!data.lineItems,
        itemsLength: rawItems.length,
        itemsPreview: rawItems.length > 0 ? JSON.stringify(rawItems[0]) : 'empty',
        allKeys: Object.keys(data).sort()
      });

      setInvoice({
        id: data.id,
        status: data.status,
        type: data.invoiceType || data.type || 'goods',  // Handle both field names
        invoiceNumber: data.invoice_number,
        issueDate: data.issue_date,
        dueDate: data.due_date,
        poDate: data.po_date,
        agreementNumber: data.agreement_number,
        poNumber: data.po_number || null,
        paymentTerms: data.payment_terms,
        client: client,
        items: rawItems.map(item => ({
          id: item.id || item._id,
          name: item.name || item.item_name,
          description: item.description || '',
          type: item.type || item.item_type || 'goods',
          quantity: item.quantity || item.qty || 1,
          rate: item.rate || item.price || item.unit_price || 0,  // Handle multiple field names
          discount: item.discount || item.item_discount || 0,
          discountType: item.discount_type || item.discountType || 'flat',
          gstRate: item.gst_rate || item.gstRate || item.tax_rate || item.tax || 0,  // Handle multiple formats
          gstType: item.gst_type || item.gstType || 'none',
          hsn: item.hsn || item.hsn_code || '',
          sac: item.sac || item.sac_code || '',
          uom: item.uom || item.unit || 'unit',
          sourceItemId: item.source_item_id || item.sourceItemId || item.item_id,
          isOverridden: item.is_overridden || item.isOverridden || false,
          // Backend calculated values (immutable for sent invoices)
          cgst_amount: item.cgst_amount || item.cgst,
          sgst_amount: item.sgst_amount || item.sgst,
          igst_amount: item.igst_amount || item.igst,
          tax: item.tax || item.tax_amount,
          amount: item.amount || item.total_amount || item.line_total,
        })),
        notes: data.notes || "",
        discount: data.discount || 0,
        discountType: data.discount_type || data.discountType || "flat",
        shipping: data.shipping || 0,
        roundOff: data.round_off || data.roundOff || 0,
        total: data.total || 0,
        balanceDue: data.balance_due || data.balanceDue || 0,
        totalPaid: data.total_paid || data.totalPaid || 0,
      });
      
      console.log('[useInvoiceDraft] Loaded invoice with', rawItems.length, 'items');

      setLastSaved(new Date(data.updated_at));
      setHasUserInput(true); // Existing invoice means user has already entered data
    } catch (err) {
      console.error("Failed to load invoice:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* ═══════════════════════════════════════════════════════════
     SAVE DRAFT (with debouncing)
     ═══════════════════════════════════════════════════════════ */
  const saveDraft = useCallback(async () => {
    if (!invoice.id) return;

    // Prevent duplicate saves
    if (isSavingRef.current) {
      console.log('[useInvoiceDraft] Save already in progress, skipping...');
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);

    try {
      const updatedInvoice = await invoiceService.save(invoice.id, {
        invoiceNumber: invoice.invoiceNumber,
        type: invoice.type,
        client: invoice.client,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        poDate: invoice.poDate,
        agreementNumber: invoice.agreementNumber,
        poNumber: invoice.poNumber,
        paymentTerms: invoice.paymentTerms,
        discount: invoice.discount,
        discountType: invoice.discountType,
        shipping: invoice.shipping,
        roundOff: invoice.roundOff,
        items: invoice.items,
        notes: invoice.notes,
        status: invoice.status,
      });

      // Update local state with server response (includes recalculated totals)
      if (updatedInvoice) {
        setInvoice((prev) => ({
          ...prev,
          total: updatedInvoice.total || 0,
          balanceDue: updatedInvoice.balance_due || 0,
          subtotal: updatedInvoice.subtotal || 0,
          taxTotal: updatedInvoice.tax_total || 0,
        }));
      }

      setLastSaved(new Date());
      hasChangesRef.current = false;
    } catch (err) {
      console.error("Failed to save invoice:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  }, [invoice]);

  /* ═══════════════════════════════════════════════════════════
     SEND INVOICE
     🔥 Products ARE created when sending (commit happens in backend)
     ═══════════════════════════════════════════════════════════ */
  const sendInvoice = useCallback(async () => {
    // If invoice hasn't been created yet, create it first then send
    let invoiceId = invoice.id;
    if (!invoiceId) {
      setIsLoading(true);
      setError(null);
      try {
        const result = await invoiceService.create({
          type: invoice.type,
          issue_date: invoice.issueDate,
          due_date: invoice.dueDate,
          payment_terms: invoice.paymentTerms,
          status: "draft",
        });
        invoiceId = result.id;
        setInvoice((prev) => ({
          ...prev,
          id: result.id,
          invoiceNumber: result.invoice_number,
        }));
      } catch (err) {
        console.error("Failed to create invoice before sending:", err);
        setError(err.message);
        setIsLoading(false);
        throw err;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      // Save latest state first to ensure items are persisted
      await invoiceService.save(invoiceId, {
        invoiceNumber: invoice.invoiceNumber,
        type: invoice.type,
        client: invoice.client,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        poDate: invoice.poDate,
        agreementNumber: invoice.agreementNumber,
        poNumber: invoice.poNumber,
        paymentTerms: invoice.paymentTerms,
        discount: invoice.discount,
        discountType: invoice.discountType,
        shipping: invoice.shipping,
        roundOff: invoice.roundOff,
        items: invoice.items,
        notes: invoice.notes,
        status: "draft",
      });

      // Now send
      const result = await invoiceService.send(invoiceId, {
        items: invoice.items,
      });

      setInvoice((prev) => ({
        ...prev,
        id: invoiceId,
        status: result.status,
      }));

      setLastSaved(new Date());
      return result;
    } catch (err) {
      console.error("Failed to send invoice:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [invoice]);

  /* ═══════════════════════════════════════════════════════════
     RESEND INVOICE
     🔥 Resends email for already sent invoice
     ═══════════════════════════════════════════════════════════ */
  const resendInvoice = useCallback(async () => {
    if (!invoice.id) {
      throw new Error("Invoice must be created before resending");
    }

    if (invoice.status !== "sent" && invoice.status !== "paid") {
      throw new Error("Only sent or paid invoices can be resent");
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await invoiceService.resend(invoice.id);
      setLastSaved(new Date());
      return result;
    } catch (err) {
      console.error("Failed to resend invoice:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, invoice.status]);

  /* ═══════════════════════════════════════════════════════════
     DELETE INVOICE
     ═══════════════════════════════════════════════════════════ */
  const deleteInvoice = useCallback(async () => {
    if (!invoice.id) {
      throw new Error("Invoice must be created before deleting");
    }

    if (invoice.status !== "draft") {
      throw new Error("Only draft invoices can be deleted");
    }

    setIsLoading(true);
    setError(null);

    try {
      await invoiceService.delete(invoice.id);
    } catch (err) {
      console.error("Failed to delete invoice:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [invoice.id, invoice.status]);

  /* ═══════════════════════════════════════════════════════════
     HELPER: CHECK IF INVOICE HAS MEANINGFUL DATA
     ═══════════════════════════════════════════════════════════ */
  const hasMeaningfulData = useCallback((invoiceData) => {
    // Check if any meaningful fields have been filled (excluding default values)
    return (
      (invoiceData.client && invoiceData.client.id) ||
      (invoiceData.items && invoiceData.items.length > 0) ||
      (invoiceData.notes && invoiceData.notes.trim().length > 0) ||
      (invoiceData.discount && invoiceData.discount > 0) ||
      (invoiceData.shipping && invoiceData.shipping > 0) ||
      (invoiceData.roundOff && invoiceData.roundOff !== 0) ||
      (invoiceData.agreementNumber && invoiceData.agreementNumber.trim().length > 0) ||
      // Only consider paymentTerms meaningful if it's different from default
      (invoiceData.paymentTerms && invoiceData.paymentTerms !== "due-on-receipt")
    );
  }, []);

  /* ═══════════════════════════════════════════════════════════
     UPDATE FIELD
     ═══════════════════════════════════════════════════════════ */
  const updateField = useCallback((field, value) => {
    setInvoice((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // Clear items when invoice type changes (goods <-> services)
      if (field === 'type' && prev.type && prev.type !== value && prev.items && prev.items.length > 0) {
        updated.items = [];
      }
      
      // Check if this is the first meaningful input
      if (!hasUserInput && hasMeaningfulData(updated)) {
        setHasUserInput(true);
      }
      
      return updated;
    });
    hasChangesRef.current = true;
  }, [hasUserInput, hasMeaningfulData]);

  /* ═══════════════════════════════════════════════════════════
     AUTO-CREATE INVOICE ON MOUNT OR FIRST INPUT
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (invoiceId) {
      loadInvoice(invoiceId);
    }
    // Don't auto-create invoice on mount - wait for user input
  }, [invoiceId]);

  // Create invoice when user first enters meaningful data
  useEffect(() => {
    if (!invoiceId && !invoice.id && hasUserInput) {
      createInvoice();
    }
  }, [invoiceId, invoice.id, hasUserInput, createInvoice]);

  /* ═══════════════════════════════════════════════════════════
     AUTOSAVE WITH DEBOUNCE
     🔥 CRITICAL: Only autosave draft invoices to prevent data corruption
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    // CRITICAL: Do not autosave sent/paid/cancelled invoices
    // This prevents overwriting existing data with empty items
    if (!invoice.id || !hasChangesRef.current || invoice.status !== 'draft') {
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout (1 second debounce)
    saveTimeoutRef.current = setTimeout(() => {
      saveDraft();
    }, 1000);

    // Cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [invoice, saveDraft]);

  /* ═══════════════════════════════════════════════════════════
     SAVE ON UNMOUNT (Critical for navigation)
     ═══════════════════════════════════════════════════════════ */
  useEffect(() => {
    return () => {
      // Save any pending changes when component unmounts
      if (invoice.id && hasChangesRef.current && !isSavingRef.current) {
        // Cancel any pending autosave to prevent race condition
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }

        // Fetch latest document to get current _rev and prevent 409 conflict
        invoiceService.get(invoice.id)
          .then((latestInvoice) => {
            // Merge our changes with latest _rev
            const dataToSave = {
              invoiceNumber: invoice.invoiceNumber,
              type: invoice.type,
              client: invoice.client,
              issueDate: invoice.issueDate,
              dueDate: invoice.dueDate,
              poDate: invoice.poDate,
              agreementNumber: invoice.agreementNumber,
              poNumber: invoice.poNumber,
              paymentTerms: invoice.paymentTerms,
              discount: invoice.discount,
              discountType: invoice.discountType,
              shipping: invoice.shipping,
              roundOff: invoice.roundOff,
              items: invoice.items,
              notes: invoice.notes,
              status: invoice.status,
            };

            // Save with latest _rev
            return invoiceService.save(invoice.id, dataToSave);
          })
          .catch((err) => {
            console.error("Failed to save on unmount:", err);
            // If it's a 409 conflict, log but don't retry (component is unmounting)
            if (err.message?.includes('conflict')) {
              console.warn('[useInvoiceDraft] Document conflict on unmount - changes may be lost');
            }
          });
      }
    };
  }, [invoice]);

  return {
    invoice,
    updateField,
    createInvoice,
    loadInvoice,
    saveDraft,
    sendInvoice,
    resendInvoice,
    deleteInvoice,
    isLoading,
    isSaving,
    lastSaved,
    error,
    hasUnsavedChanges: hasChangesRef.current,
  };
}
